import React, { Fragment, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "@data/GlobalData.js";
import ImageButton from "@controls/ImageButton.js";
import LanguageEntry from "@controls/LanguageEntry.js";
import { LoadClientSideOnly } from "@controls/LazyLoader.js";
import { LinkNav } from "@controls/LinkNav.js";
import MenuItem from "@controls/MenuItem.js";
import MessageCount from "@controls/MessageCount.js";
import { useGenericStore } from "@customHooks/useGenericStore.js";
import {
  CFW_CURRENT_TRANSLATION,
  CFW_LOAD_MENU,
  DIALOGHOLDER,
  PRODUCTCODES_UPUBLISHME,
  PRODUCTCODES_URTECHCITY,
  PRODUCTCODES_URTECHPOGO,
  PRODUCTCODES_URTECHPOS,
  PRODUCTCODES_URTECHSHOP,
  Response_TranslationData,
} from "@genericComponents/CFW.js";
import { GeneratePath } from "@genericComponents/Common.js";
import Loading from "@genericComponents/Loading.js";
import { MaintainDialog } from "@genericComponents/PFW.js";
import { PostData } from "@genericComponents/SFW.js";
import {
  GetState,
  GetStateField,
  globalVariables,
  SetState,
} from "@genericComponents/StoreToolkit.js";
import { User } from "@genericComponents/User.js";
import { UnregisterServiceWorkers } from "@src/serviceWorkerRegistration.js";
const useProductLanguageCode =
  await import("@customHooks/useProductLanguageCode.js").then(
    (mod) => mod.useProductLanguageCode,
  );
const HydrationLoadScript = lazy(
  () => import("@controls/HydrationLoadScripts.js"),
);
const HeaderCompany = lazy(() => import("@headers/Company.js"));
const HeaderJobPortal = lazy(() => import("@headers/JobPortal.js"));
const HeaderPOGO = lazy(() => import("@headers/POGO.js"));
const HeaderPOS = lazy(() => import("@headers/POS.js"));
const HeaderShopper = lazy(() => import("@headers/Shopper.js"));
const HeaderUPublishMe = lazy(() => import("@headers/UPublishMe.js"));
const cssImporter = await import("@asyncImports/cssImporter.js").then(
  (mod) => mod.cssImporter,
);

const ChangeLanguage = (pathname, navigate) => {
  const languageState = "LANGUAGE_STATE";
  const postEvt = () => {
    let urlHasLanguage = false;
    const languageCode = GetStateField(languageState, "LanguageCode");
    const responseState = `${Response_TranslationData}-${languageCode}`;
    const pathNames = pathname
      .split("/")
      .map((itm) => {
        if (itm.length === 2) {
          urlHasLanguage = true;
          return languageCode;
        }
        return itm;
      })
      .filter((itm) => !!itm);
    if (!urlHasLanguage) {
      pathNames.splice(2, 0, languageCode);
    }
    if (!GetState(responseState)) {
      PostData(
        `${API_URL}GetTranslations/${languageCode}`,
        null,
        responseState,
        (data) => {
          const translations = {};
          Object.keys(data).forEach((langItem) => {
            translations[langItem.replace(/\D/g, "")] =
              data[langItem].TranslationDescription;
          });
          window.initScripts[responseState] = translations;
          SetState(responseState, translations);
          globalVariables[CFW_CURRENT_TRANSLATION] = responseState;
          navigate(`/${pathNames.join("/")}`);
        },
      );
    } else {
      globalVariables[CFW_CURRENT_TRANSLATION] =
        `${Response_TranslationData}-${languageCode}`;
      navigate(`/${pathNames.join("/")}`);
    }
  };
  const dialogParam = { ...MaintainDialog };
  window.children = <LanguageEntry state={languageState} tran="620" required />;
  dialogParam.title = "571";
  dialogParam.pb = "198";
  window.pcb = postEvt;
  dialogParam.shownb = true;
  SetState(DIALOGHOLDER, dialogParam);
};
export const Header = () => {
  const navigate = useNavigate();
  const { languageCode, productCode, title, pathName } =
    useProductLanguageCode(true);
  const GetHeader = () => {
    const loadMenu = useGenericStore(CFW_LOAD_MENU);
    if (loadMenu) {
      switch (productCode.toUpperCase()) {
        case PRODUCTCODES_URTECHPOS:
          return <HeaderPOS />;
        case PRODUCTCODES_URTECHSHOP:
          return <HeaderShopper />;
        case PRODUCTCODES_URTECHCITY:
          return <HeaderCompany />;
        case PRODUCTCODES_UPUBLISHME:
          return <HeaderUPublishMe />;
        case PRODUCTCODES_URTECHPOGO:
          return <HeaderPOGO />;
        default:
          return <HeaderJobPortal />;
      }
    }
    return <Fragment />;
  };
  if (
    pathName.toLowerCase().includes("/dailytimerecord/") ||
    pathName.toLowerCase().includes("/auth/linkedin/callback")
  ) {
    return <Fragment />;
  } else {
    return (
      <header role="banner" className="width100">
        <HydrationLoadScript
          postEvt={() => {
            import("@css/headerStyles.css");
            cssImporter(
              "ui-icon-key-white",
              "ui-icon-mail-open-white",
              "ui-icon-person-white",
              "ui-icon-flag-white",
              "ui-icon-locked-white",
            );
          }}
        />
        <div className="menuHeader">
          <div className="logo">
            <div className="logoTitle">{title}</div>
          </div>
          <div className="mainButtonContainer">
            <span>
              {!User.IsLoggedIn() ? (
                <span>
                  <LinkNav
                    tran={"15"}
                    exact="true"
                    to={GeneratePath(languageCode, productCode, "SignIn")}
                  >
                    <ImageButton
                      buttonClassName="linkButton"
                      className="ui-icon-key-white"
                      tran="15"
                      hiddenOption={2}
                    ></ImageButton>
                  </LinkNav>
                </span>
              ) : (
                <span className="initiallyHidden">
                  <LinkNav
                    tran={"269"}
                    exact="true"
                    to={GeneratePath(languageCode, productCode, "Message")}
                  >
                    <LoadClientSideOnly>
                      <div className="messageCount">
                        <ImageButton
                          buttonClassName="linkButton"
                          className="ui-icon-mail-open-white"
                          tran="269"
                          hiddenOption={1}
                        ></ImageButton>
                        <MessageCount />
                      </div>
                    </LoadClientSideOnly>
                  </LinkNav>
                  <ImageButton
                    buttonClassName="linkButton"
                    clicker={() => {
                      UnregisterServiceWorkers(
                        languageCode,
                        productCode,
                        true,
                        true,
                      );
                    }}
                    className="ui-icon-locked-white"
                    tran="39"
                    hiddenOption={2}
                  ></ImageButton>
                </span>
              )}
              <LinkNav
                tran={"22"}
                exact="true"
                to={GeneratePath(languageCode, productCode, "UpdateProfile")}
              >
                <ImageButton
                  buttonClassName="linkButton"
                  primary={!User.IsLoggedIn()}
                  className="ui-icon-person-white"
                  tran={User.IsLoggedIn() ? "22" : "16"}
                  hiddenOption={2}
                ></ImageButton>
              </LinkNav>
            </span>
            <ImageButton
              clicker={() => {
                ChangeLanguage(pathName, navigate);
              }}
              className="ui-icon-flag-white"
              tran="73"
              hiddenOption={1}
            />
            <nav role="navigation" className="headerNav">
              <ul
                className="menu"
                onMouseEnter={() => {
                  SetState(CFW_LOAD_MENU, true);
                }}
                onClick={() => {
                  SetState(CFW_LOAD_MENU, true);
                }}
              >
                <MenuItem
                  onClick={(e) => e.preventDefault()}
                  className="ui-icon-menu mainMenu"
                  tran="678"
                  hiddenOnMobile={true}
                  important="true"
                >
                  <Suspense fallback={<Loading />}>
                    <GetHeader />
                  </Suspense>
                </MenuItem>
              </ul>
            </nav>
          </div>
        </div>
      </header>
    );
  }
};
